import Guid from "@/scripts/guid"
import { get, set, cloneDeep } from "lodash"
import { createDefaultInsight, Insight } from "."

export interface InsightDuplicationOption {
	id: string
    title: string
    propertyPaths: string[]
}

export const insightDuplicationOptions: InsightDuplicationOption[] = [
	{
		id: "folder",
		title: "Folder",
		propertyPaths: ["folder"]
	},
	{
		id: "displayColumns",
		title: "Columns",
		propertyPaths: ["displayColumns", "columns"]
	},
	{
		id: "defaultView",
		title: "Default View",
		propertyPaths: ["defaultView"]
	},
	{
		id: "filtering",
		title: "Filters",
		propertyPaths: ["clauses"]
	},
	{
		id: "grouping",
		title: "Grouping",
		propertyPaths: [
			"groupBy",
			"groupByOption",
			"groupByDataType",
			"groupByLabel",
			"groupByDateFormat",
			"groupingOptions",
			"showCalculatedDateRange",
			"showCalculatedUserStatus"]
	},
	{
		id: "sorting",
		title: "Sorting",
		propertyPaths: ["sortBy", "sortByLabel", "sortByDataType", "sortByDescending"]
	},
	{
		id: "parameters",
		title: "Parameters",
		propertyPaths: ["dynamicParameters", "includeGlobalParameters"]
	},
	{
		id: "calculations",
		title: "Calculations",
		propertyPaths: ["calculations", "calculationTotals", "includeCalculationsFrom"]
	},
	{
		id: "forecasting",
		title: "Forecasting",
		propertyPaths: ["includeForecast", "forecastBy"]
	}
]

export const duplicateInsight = (insight: Insight, optionIdsToDefault: string[]): Insight => {
	const optionsToDefault = insightDuplicationOptions.filter(o => optionIdsToDefault.includes(o.id))
	const duplicatedInsight = cloneDeep(insight) as Insight
	duplicatedInsight.id = undefined
	duplicatedInsight.title = `${duplicatedInsight.title} [Duplicate]`
	duplicatedInsight.lastUpdated = undefined

	duplicatedInsight.clauses.forEach(clause => { clause.id = undefined })

	// Assign calculations new Keys
	duplicatedInsight.calculations.forEach(calculation => {
		let newKey = Guid.newGuid()

		// Check added and subtracted lists for old calculation key and update to new key
		duplicatedInsight.calculationTotals!.forEach(calculationTotal => {
			let addedCalcIndex = calculationTotal.calculationList.findIndex(key => key === calculation.key)
			if (addedCalcIndex !== -1 && calculationTotal.calculationList[addedCalcIndex]) {
				calculationTotal.calculationList[addedCalcIndex] = newKey
			}

			let subtractCalcIndex = calculationTotal.subtractCalculationList.findIndex(key => key === calculation.key)
			if (subtractCalcIndex !== -1 && calculationTotal.subtractCalculationList[subtractCalcIndex]) {
				calculationTotal.subtractCalculationList[subtractCalcIndex] = newKey
			}
		})
		calculation.key = newKey
	})

	duplicatedInsight.calculationTotals!.forEach(calculationTotal => { calculationTotal.key = Guid.newGuid() })

	if (optionsToDefault.length === 0) {
		return duplicatedInsight
	}

	const defaultInsight = createDefaultInsight()
	for (let optionToDefault of optionsToDefault) {
		for (let propertyPath of optionToDefault.propertyPaths) {
			const defaultValue = get(defaultInsight, propertyPath)
			set(duplicatedInsight, propertyPath, defaultValue)
		}
	}

	return duplicatedInsight
}
